import { create } from '@bufbuild/protobuf';
import {
  FaultToleranceConfigSchema,
  TaskFaultToleranceSchema,
  StepFaultToleranceSchema,
  StepTypePolicySchema,
  ModelFailoverSchema,
  StepType,
  type FaultToleranceConfig,
} from '@/proto/agentplatform/v1/agent_pb';
import {
  DEFAULT_FAULT_TOLERANCE,
  type FaultToleranceFormState,
  type StepTypePolicyFormState,
} from './agentFormTypes';

/** 单个步骤类型表单态 → proto StepTypePolicy（含 MODEL 的 model_failover）；关闭的子项不写明细 */
function buildStepPolicy(stepType: StepType, s: StepTypePolicyFormState) {
  return create(StepTypePolicySchema, {
    stepType,
    attemptTimeoutSeconds: s.attemptTimeoutSeconds,
    retryEnabled: s.retryEnabled,
    // 重试关闭时不保存次数/间隔
    ...(s.retryEnabled
      ? { maxRetries: s.maxRetries, retryIntervalSeconds: s.retryIntervalSeconds }
      : {}),
    ...(stepType === StepType.MODEL
      ? {
          modelFailover: create(
            ModelFailoverSchema,
            // Failover 关闭时不保存备用模型池
            s.failoverEnabled ? { enabled: true, backupModels: s.backupModels } : { enabled: false }
          ),
        }
      : {}),
  });
}

/** 表单态 → proto FaultToleranceConfig（挂 advanced_config.fault_tolerance；关闭的分区不写明细） */
export function buildFaultToleranceConfig(form: FaultToleranceFormState): FaultToleranceConfig {
  // 步骤级关闭时不保存任何步骤策略；开启时仅保存被勾选的类型
  const policies = [];
  if (form.stepEnabled) {
    if (form.model.selected) policies.push(buildStepPolicy(StepType.MODEL, form.model));
    if (form.tool.selected) policies.push(buildStepPolicy(StepType.TOOL, form.tool));
  }

  return create(FaultToleranceConfigSchema, {
    enabled: form.enabled,
    // 任务级关闭时不保存超时/重试明细
    task: create(
      TaskFaultToleranceSchema,
      form.taskEnabled
        ? {
            enabled: true,
            totalTimeoutSeconds: form.taskTotalTimeoutSeconds,
            retryEnabled: form.taskRetryEnabled,
            ...(form.taskRetryEnabled
              ? {
                  maxRetries: form.taskMaxRetries,
                  retryIntervalSeconds: form.taskRetryIntervalSeconds,
                }
              : {}),
          }
        : { enabled: false }
    ),
    step: create(StepFaultToleranceSchema, {
      enabled: form.stepEnabled,
      policies,
    }),
    configurable: form.configurable,
  });
}

/**
 * 无容错配置时的兜底表单态：沿用默认的子项数值，但**总开关关闭**。
 * 关键：新建表单的「默认开启」来自 DEFAULT_FAULT_TOLERANCE 的直接使用（CreateAgent 初始 state），
 * 而“读取一份不存在的配置”语义上是「未配置=关闭」。二者必须区分，否则会出现
 * 关闭容错 → 落库时省略整块 → 回读又按默认开启的错位（关闭不生效）。
 */
function absentFaultTolerance(): FaultToleranceFormState {
  return { ...structuredClone(DEFAULT_FAULT_TOLERANCE), enabled: false };
}

/** proto FaultToleranceConfig → 表单态；无字段时视为「未配置=关闭」（子项数值仍给默认，便于开启后编辑） */
export function parseFaultToleranceConfig(
  config: FaultToleranceConfig | undefined
): FaultToleranceFormState {
  if (!config) return absentFaultTolerance();

  const byType = new Map<StepType, ReturnType<typeof buildStepPolicy>>();
  (config.step?.policies || []).forEach((p) => byType.set(p.stepType, p));

  const readPolicy = (stepType: StepType): StepTypePolicyFormState => {
    const base = structuredClone(
      stepType === StepType.MODEL ? DEFAULT_FAULT_TOLERANCE.model : DEFAULT_FAULT_TOLERANCE.tool
    );
    const p = byType.get(stepType);
    if (!p) return { ...base, selected: false };
    return {
      selected: true,
      attemptTimeoutSeconds: p.attemptTimeoutSeconds || base.attemptTimeoutSeconds,
      retryEnabled: p.retryEnabled,
      maxRetries: p.maxRetries || base.maxRetries,
      retryIntervalSeconds: p.retryIntervalSeconds || base.retryIntervalSeconds,
      failoverEnabled: p.modelFailover?.enabled ?? false,
      backupModels: p.modelFailover?.backupModels ?? [],
    };
  };

  return {
    enabled: config.enabled,
    taskEnabled: config.task?.enabled ?? DEFAULT_FAULT_TOLERANCE.taskEnabled,
    taskTotalTimeoutSeconds:
      config.task?.totalTimeoutSeconds || DEFAULT_FAULT_TOLERANCE.taskTotalTimeoutSeconds,
    taskRetryEnabled: config.task?.retryEnabled ?? false,
    taskMaxRetries: config.task?.maxRetries || DEFAULT_FAULT_TOLERANCE.taskMaxRetries,
    taskRetryIntervalSeconds:
      config.task?.retryIntervalSeconds || DEFAULT_FAULT_TOLERANCE.taskRetryIntervalSeconds,
    stepEnabled: config.step?.enabled ?? DEFAULT_FAULT_TOLERANCE.stepEnabled,
    model: readPolicy(StepType.MODEL),
    tool: readPolicy(StepType.TOOL),
    configurable: config.configurable,
  };
}

// ---------------------------------------------------------------------------
// 实例覆盖槽位：与后端 plugin_faulttolerance.go 的 faultToleranceSlotDefaults 对称。
// 后端 WorkGraph 模板对 configurable 的容错配置留三个槽位，运行期用
// instance_params[<slot key>] 覆盖；实例侧只持久化这三个槽位 JSON（不再保存整份配置），
// 保存时由 buildFaultToleranceSlotParams 拆分，回读时由 parseFaultToleranceSlotParams 还原。
// ---------------------------------------------------------------------------

/** 模型调用重试中间件槽位 key（llm-agent ModelCallRetryMiddlewareConfig） */
export const FT_SLOT_MODEL_CALL_RETRY = 'model_call_retry';
/** 工具调用容错中间件槽位 key（llm-agent ToolErrorShieldMiddlewareConfig） */
export const FT_SLOT_TOOL_ERROR_SHIELD = 'tool_error_shield';
/** 任务级容错槽位 key（llm-agent TaskFaultToleranceConfig） */
export const FT_SLOT_TASK_FAULT_TOLERANCE = 'task_fault_tolerance';

export const FT_SLOT_KEYS = [
  FT_SLOT_MODEL_CALL_RETRY,
  FT_SLOT_TASK_FAULT_TOLERANCE,
  FT_SLOT_TOOL_ERROR_SHIELD,
] as const;

interface StepRetryJson {
  enabled: boolean;
  max_retries: number;
  retry_interval_seconds: number;
  attempt_timeout_seconds?: number;
}

/**
 * 槽位「禁用」JSON：实例关闭某项时必须写入显式零值，而不能留空——
 * 运行时对空字符串直接跳过并回退模板默认，会导致“实例关闭不生效”。
 * task_fault_tolerance 不带 total_timeout_seconds（缺省 = 不限时；0 会被当成立即超时）。
 */
function disabledSlotJson(key: string): string {
  if (key === FT_SLOT_TASK_FAULT_TOLERANCE) {
    return JSON.stringify({ max_retries: 0, retry_interval_seconds: 0 });
  }
  return JSON.stringify({ enabled: false, max_retries: 0, retry_interval_seconds: 0 });
}

function buildStepRetryJson(stepEnabled: boolean, p: StepTypePolicyFormState): StepRetryJson {
  const out: StepRetryJson = {
    enabled: stepEnabled,
    max_retries: stepEnabled && p.retryEnabled ? p.maxRetries : 0,
    retry_interval_seconds: p.retryIntervalSeconds,
  };
  if (p.attemptTimeoutSeconds > 0) {
    out.attempt_timeout_seconds = p.attemptTimeoutSeconds;
  }
  return out;
}

/**
 * 表单态 → 三个槽位 JSON（实例覆盖写入 instance_params[<slot key>]）。
 * 始终返回全部三个 key：开启项按表单构造，关闭项写禁用零值，保证实例能覆盖模板的任意一项。
 */
export function buildFaultToleranceSlotParams(
  form: FaultToleranceFormState
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of FT_SLOT_KEYS) out[key] = disabledSlotJson(key);
  if (!form.enabled) return out;

  if (form.stepEnabled) {
    if (form.model.selected) {
      const mc: StepRetryJson & { backup_models?: Array<{ name: string }> } = buildStepRetryJson(
        true,
        form.model
      );
      if (form.model.failoverEnabled) {
        const backups = form.model.backupModels.filter((name) => !!name).map((name) => ({ name }));
        if (backups.length > 0) mc.backup_models = backups;
      }
      out[FT_SLOT_MODEL_CALL_RETRY] = JSON.stringify(mc);
    }
    if (form.tool.selected) {
      out[FT_SLOT_TOOL_ERROR_SHIELD] = JSON.stringify(buildStepRetryJson(true, form.tool));
    }
  }

  if (form.taskEnabled) {
    out[FT_SLOT_TASK_FAULT_TOLERANCE] = JSON.stringify({
      max_retries: form.taskRetryEnabled ? form.taskMaxRetries : 0,
      retry_interval_seconds: form.taskRetryIntervalSeconds,
      total_timeout_seconds: form.taskTotalTimeoutSeconds,
    });
  }
  return out;
}

/** 实例 params 是否带有任一容错槽位（用于判定「实例已覆盖」） */
export function hasFaultToleranceSlotParams(params: Record<string, string> | undefined): boolean {
  return FT_SLOT_KEYS.some((key) => !!params?.[key]);
}

type SlotObject = Record<string, unknown>;

function parseSlotObject(raw: string | undefined): SlotObject | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as SlotObject) : null;
  } catch {
    return null;
  }
}

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/** 槽位里的 StepRetryConfig 片段 → 步骤类型表单态；未选中 / enabled=false 时基于 base 置 selected=false */
function readStepRetry(
  obj: SlotObject | null | undefined,
  base: StepTypePolicyFormState
): StepTypePolicyFormState {
  if (!obj || obj.enabled !== true) return { ...structuredClone(base), selected: false };
  const maxRetries = num(obj.max_retries);
  return {
    ...structuredClone(base),
    selected: true,
    // 槽位没有 retry_enabled，预算 > 0 即视为开启重试
    retryEnabled: maxRetries > 0,
    maxRetries: maxRetries || base.maxRetries,
    retryIntervalSeconds: num(obj.retry_interval_seconds) || base.retryIntervalSeconds,
    attemptTimeoutSeconds: num(obj.attempt_timeout_seconds) || base.attemptTimeoutSeconds,
  };
}

/**
 * 三个槽位 JSON（instance_params）→ 表单态，是 buildFaultToleranceSlotParams 的逆向。
 * 以模板表单态 template 为底：enabled / configurable 与关闭项的编辑默认值均沿用模板；
 * 无任何槽位 key 时直接返回 template（视为继承模板）。
 */
export function parseFaultToleranceSlotParams(
  params: Record<string, string> | undefined,
  template: FaultToleranceFormState
): FaultToleranceFormState {
  if (!hasFaultToleranceSlotParams(params)) return template;
  const form = structuredClone(template);

  // model_call_retry
  const mc = parseSlotObject(params?.[FT_SLOT_MODEL_CALL_RETRY]);
  form.model = readStepRetry(mc, template.model);
  if (form.model.selected) {
    const backups = Array.isArray(mc?.backup_models)
      ? (mc!.backup_models as unknown[])
          .map((b) => (b && typeof b === 'object' ? (b as SlotObject).name : undefined))
          .filter((n): n is string => typeof n === 'string' && n.length > 0)
      : [];
    form.model.failoverEnabled = backups.length > 0;
    form.model.backupModels = backups;
  } else {
    form.model.failoverEnabled = false;
    form.model.backupModels = [];
  }

  // tool_error_shield：顶层为 TOOL
  const ts = parseSlotObject(params?.[FT_SLOT_TOOL_ERROR_SHIELD]);
  const toolPresent = !!ts && ts.enabled === true && typeof ts.max_retries === 'number';
  form.tool = readStepRetry(toolPresent ? ts : null, template.tool);

  form.stepEnabled = form.model.selected || form.tool.selected;

  // task_fault_tolerance：无 total_timeout_seconds 即为禁用
  const tft = parseSlotObject(params?.[FT_SLOT_TASK_FAULT_TOLERANCE]);
  const totalTimeout = num(tft?.total_timeout_seconds);
  form.taskEnabled = totalTimeout > 0;
  if (form.taskEnabled) {
    const maxRetries = num(tft?.max_retries);
    form.taskTotalTimeoutSeconds = totalTimeout;
    form.taskRetryEnabled = maxRetries > 0;
    form.taskMaxRetries = maxRetries || template.taskMaxRetries;
    form.taskRetryIntervalSeconds =
      num(tft?.retry_interval_seconds) || template.taskRetryIntervalSeconds;
  }

  return form;
}

/** 校验容错表单，返回错误信息（null 表示通过）。总开关关闭时不校验。 */
export function validateFaultTolerance(form: FaultToleranceFormState): string | null {
  if (!form.enabled) return null;
  if (!form.taskEnabled && !form.stepEnabled) {
    return '容错总开关开启时，任务级和步骤级容错至少开启一项';
  }
  if (form.stepEnabled && !form.model.selected && !form.tool.selected) {
    return '开启步骤级容错后至少选择一种步骤类型';
  }
  const inRange = (n: number) => n >= 1 && n <= 10;
  if (form.taskEnabled) {
    if (form.taskTotalTimeoutSeconds <= 0 || form.taskTotalTimeoutSeconds > 604800) {
      return '任务总超时必须为正整数且不超过 168 小时';
    }
    if (
      form.taskRetryEnabled &&
      (!inRange(form.taskMaxRetries) || !inRange(form.taskRetryIntervalSeconds))
    ) {
      return '任务最大重试次数与重试间隔必须为 1-10';
    }
  }
  if (form.stepEnabled) {
    for (const [label, p] of [
      ['模型调用', form.model],
      ['工具调用', form.tool],
    ] as const) {
      if (!p.selected) continue;
      if (p.attemptTimeoutSeconds <= 0 || p.attemptTimeoutSeconds > 259200) {
        return `${label}的单次步骤超时必须为正整数且不超过 72 小时`;
      }
      if (p.retryEnabled && (!inRange(p.maxRetries) || !inRange(p.retryIntervalSeconds))) {
        return `${label}的步骤重试次数与间隔必须为 1-10`;
      }
    }
    // 模型降级切换开启后备用模型必填（判空口径与 buildFaultToleranceSlotParams 的 filter(Boolean) 一致）
    if (
      form.model.selected &&
      form.model.failoverEnabled &&
      form.model.backupModels.filter(Boolean).length === 0
    ) {
      return '开启模型降级切换后请至少选择一个备用模型';
    }
  }
  return null;
}
